import { useState } from "react";
import { Star } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

export const StarRating = ({ value, onChange, readOnly = false, testid }) => {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex items-center gap-1" data-testid={testid}>
      {[1, 2, 3, 4, 5].map((n) => {
        const active = (hover || value) >= n;
        return (
          <button
            key={n}
            type="button"
            disabled={readOnly}
            data-testid={testid ? `${testid}-star-${n}` : undefined}
            onMouseEnter={() => !readOnly && setHover(n)}
            onMouseLeave={() => !readOnly && setHover(0)}
            onClick={() => !readOnly && onChange(n)}
            className={`transition-transform ${readOnly ? "cursor-default" : "hover:scale-110"}`}
          >
            <Star
              className={`h-6 w-6 ${active ? "fill-amber-400 text-amber-400" : "text-slate-300"}`}
              strokeWidth={1.6}
            />
          </button>
        );
      })}
    </div>
  );
};

const Row = ({ label, children }) => (
  <div className="flex flex-wrap items-center justify-between gap-2">
    <Label className="text-sm text-brand-ink">{label}</Label>
    {children}
  </div>
);

export const FeedbackForm = ({ jobId, initial, onSaved }) => {
  const [form, setForm] = useState({
    overall: initial?.overall || 0,
    quality: initial?.quality || 0,
    staff_behaviour: initial?.staff_behaviour || 0,
    recommend: initial?.recommend ?? null,
    comments: initial?.comments || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.overall || !form.quality || !form.staff_behaviour)
      return toast.error("Please give all three star ratings");
    if (form.recommend === null) return toast.error("Select whether the customer would recommend");
    setBusy(true);
    try {
      const { data } = await api.post(`/jobs/${jobId}/feedback`, {
        overall: form.overall,
        quality: form.quality,
        staff_behaviour: form.staff_behaviour,
        recommend: form.recommend,
        comments: form.comments,
      });
      toast.success("Feedback saved");
      onSaved?.(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="feedback-form">
      <Row label="Overall satisfaction">
        <StarRating value={form.overall} onChange={(v) => set("overall", v)} testid="fb-overall" />
      </Row>
      <Row label="Quality of work">
        <StarRating value={form.quality} onChange={(v) => set("quality", v)} testid="fb-quality" />
      </Row>
      <Row label="Staff behaviour">
        <StarRating value={form.staff_behaviour} onChange={(v) => set("staff_behaviour", v)} testid="fb-staff" />
      </Row>
      <Row label="Would recommend?">
        <div className="flex gap-2">
          {[["Yes", true], ["No", false]].map(([lbl, val]) => (
            <Button
              key={lbl}
              type="button"
              size="sm"
              variant={form.recommend === val ? "default" : "outline"}
              data-testid={`fb-recommend-${lbl.toLowerCase()}`}
              onClick={() => set("recommend", val)}
            >
              {lbl}
            </Button>
          ))}
        </div>
      </Row>
      <div>
        <Label className="text-sm text-brand-ink">Comments</Label>
        <Textarea
          className="mt-1.5"
          rows={3}
          data-testid="fb-comments"
          placeholder="Notes from the call / interaction with the customer"
          value={form.comments}
          onChange={(e) => set("comments", e.target.value)}
        />
      </div>
      <Button className="w-full" data-testid="fb-save-btn" disabled={busy} onClick={save}>
        {initial ? "Update feedback" : "Submit feedback"}
      </Button>
    </div>
  );
};
